import { useCallback, useEffect, useState } from 'react';
import { useUrlState } from '@/lib/useUrlState';
import { aktePfad } from '@/lib/contact-path';
import { Link } from 'react-router-dom';
import type { EmailMessage, Stage } from '@epasu/core';
import { CheckCircle2, Handshake, MailOpen, Reply, UserCheck, XCircle } from 'lucide-react';
import { crm, type CampaignRow, type MailRow } from '@/lib/crm-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ReplyDialog } from '@/components/ReplyDialog';
import { DealDialog } from '@/components/DealDialog';
import { ReplySentimentButtons, SENTIMENT_LABEL } from '@/components/ReplySentimentButtons';

interface DealVorbereitung {
  row: MailRow;
  stage: Stage;
  stages: Stage[];
  pipelineId: string;
}

/**
 * Antworten auf Kampagnenmails — die Stelle, an der aus einer Reaktion
 * etwas wird: weiterschreiben, den Lead zum Kontakt befördern, einen Deal
 * anlegen, die Antwort einordnen oder die Sequenz beenden.
 */
export function RepliesPage() {
  const [kampagnen, setKampagnen] = useState<CampaignRow[]>([]);
  // In der URL, damit der Filter den Ausflug in eine Lead-Akte uebersteht.
  const [campaignId, setCampaignId] = useUrlState<string>('kampagne', '');
  const [rows, setRows] = useState<MailRow[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);

  const [antworten, setAntworten] = useState<EmailMessage | null>(null);
  const [beenden, setBeenden] = useState<MailRow | null>(null);
  const [dealVorbereitung, setDealVorbereitung] = useState<DealVorbereitung | null>(null);
  const [dealLaedt, setDealLaedt] = useState<string | null>(null);

  useEffect(() => {
    void crm.campaigns.list().then(setKampagnen);
  }, []);

  const load = useCallback(() => {
    setFehler(null);
    crm.mailbox
      .list({ onlyOutreachReplies: true, ...(campaignId ? { campaignId } : {}) })
      .then(setRows, (err: unknown) =>
        setFehler(err instanceof Error ? err.message : 'Laden fehlgeschlagen'),
      );
  }, [campaignId]);
  useEffect(load, [load]);

  const uebernehmen = async (row: MailRow) => {
    if (!row.contactId) return;
    setAktionsFehler(null);
    try {
      await crm.contacts.setLifecycle(row.contactId, 'kontakt');
      load();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : 'Übernehmen fehlgeschlagen');
    }
  };

  const oeffnenZumAntworten = async (row: MailRow) => {
    if (!row.threadId) return;
    setAktionsFehler(null);
    try {
      const thread = await crm.mailbox.thread(row.threadId);
      const nachricht = thread.find((m) => m.id === row.id) ?? thread[thread.length - 1];
      if (nachricht) setAntworten(nachricht);
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : 'Nachricht konnte nicht geladen werden');
    }
  };

  const dealVorbereiten = async (row: MailRow) => {
    if (!row.contactId) return;
    setDealLaedt(row.id);
    setAktionsFehler(null);
    try {
      const kontakt = await crm.contacts.get(row.contactId);
      if (!kontakt) throw new Error('Kontakt nicht gefunden.');
      const pipelines = await crm.pipelines.list(kontakt.audienceId);
      const pipeline = pipelines[0];
      if (!pipeline) throw new Error('Für diese Zielgruppe gibt es noch keine Pipeline.');
      const stages = await crm.pipelines.stages(pipeline.id);
      const stage = stages[0];
      if (!stage) throw new Error('Diese Pipeline hat noch keine Stufe.');
      setDealVorbereitung({ row, stage, stages, pipelineId: pipeline.id });
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : 'Deal konnte nicht vorbereitet werden');
    } finally {
      setDealLaedt(null);
    }
  };

  return (
    <div>
      <PageHeader>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">Antworten</h1>
          <Select
            className="ml-auto h-9 w-56"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            aria-label="Kampagne"
          >
            <option value="">Alle Kampagnen</option>
            {kampagnen.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </Select>
        </div>
      </PageHeader>

      {fehler && (
        <Card className="mb-4 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {fehler}
        </Card>
      )}
      {aktionsFehler && (
        <Card className="mb-4 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {aktionsFehler}
        </Card>
      )}

      {rows === null ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          <MailOpen className="mx-auto mb-3 h-6 w-6" />
          Noch keine Antwort auf eine Kampagnenmail.
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {rows.map((row) => (
            <div key={row.id} className="space-y-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="w-44 shrink-0 truncate font-medium">
                  {row.contactId ? (
                    <Link to={aktePfad(row.contactId, 'lead')} className="text-brand hover:underline">
                      {row.contactName ?? row.fromAddress ?? 'Kontakt'}
                    </Link>
                  ) : (
                    (row.contactName ?? row.fromAddress ?? 'Unbekannt')
                  )}
                </span>
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {row.organizationName ?? '—'}
                </span>
                <span className="w-40 shrink-0 truncate text-muted-foreground">
                  {row.campaignName ?? '—'}
                </span>
                <span className="min-w-0 flex-1 truncate" title={row.snippet ?? undefined}>
                  {row.subject || '(ohne Betreff)'}
                </span>
                {row.replySentiment && (
                  <Badge variant={row.replySentiment === 'positiv' ? 'success' : 'danger'}>
                    {SENTIMENT_LABEL[row.replySentiment]}
                  </Badge>
                )}
              </div>

              {row.snippet && (
                <p className="truncate text-xs text-muted-foreground">{row.snippet}</p>
              )}

              <div className="flex flex-wrap gap-1.5">
                <Button variant="outline" size="sm" onClick={() => void oeffnenZumAntworten(row)}>
                  <Reply className="mr-1.5 h-4 w-4" />
                  Antworten
                </Button>
                {row.contactId && (
                  <Button variant="outline" size="sm" onClick={() => void uebernehmen(row)}>
                    <UserCheck className="mr-1.5 h-4 w-4" />
                    Als Kontakt übernehmen
                  </Button>
                )}
                {row.contactId && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={dealLaedt === row.id}
                    onClick={() => void dealVorbereiten(row)}
                  >
                    <Handshake className="mr-1.5 h-4 w-4" />
                    {dealLaedt === row.id ? 'Einen Moment …' : 'Deal anlegen'}
                  </Button>
                )}
                <ReplySentimentButtons
                  messageId={row.id}
                  value={row.replySentiment}
                  onChanged={load}
                  onError={setAktionsFehler}
                />
                {/*
                  Eine Antwort beendet die Sequenz als Antwort, nicht als
                  „kalt“ — sonst steht sie hier, aber die Kampagne zaehlt sie
                  nicht. Frueher setzte der Knopf die Uhr nur auf „kalt“, und
                  an den Zahlen der Kampagne aenderte sich nichts.
                */}
                {row.outreachMessageId &&
                  (row.outreachFollowupState === 'beantwortet' ? (
                    <Badge variant="success" className="self-center">
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      In Kampagne als Antwort gezählt
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setBeenden(row)}>
                      <XCircle className="mr-1.5 h-4 w-4" />
                      Als Antwort zählen &amp; Sequenz beenden
                    </Button>
                  ))}
              </div>
            </div>
          ))}
        </Card>
      )}

      {!!rows?.length && (
        <p className="mt-3 text-xs text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'Antwort' : 'Antworten'}
        </p>
      )}

      {antworten && (
        <ReplyDialog
          message={antworten}
          onClose={() => setAntworten(null)}
          onSent={() => {
            setAntworten(null);
            load();
          }}
        />
      )}

      {beenden && beenden.outreachMessageId && (
        <ConfirmDialog
          title="Als Antwort zählen?"
          confirmLabel="Als Antwort zählen"
          onCancel={() => setBeenden(null)}
          onConfirm={async () => {
            try {
              await crm.followups.markOutreachAnswered(beenden.outreachMessageId as string);
            } catch (err) {
              setAktionsFehler(err instanceof Error ? err.message : 'Vermerken fehlgeschlagen');
            }
            setBeenden(null);
            load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Für {beenden.contactName ?? beenden.fromAddress ?? 'diesen Kontakt'} wird nicht
            mehr nachgefasst, und die Kampagne zählt die Antwort. Weitere Ansprechpartner
            derselben Firma werden in dieser Kampagne übersprungen.
          </p>
        </ConfirmDialog>
      )}

      {dealVorbereitung && (
        <DealDialog
          deal={null}
          stage={dealVorbereitung.stage}
          stages={dealVorbereitung.stages}
          pipelineId={dealVorbereitung.pipelineId}
          sourceCampaignId={dealVorbereitung.row.campaignId}
          defaultContactId={dealVorbereitung.row.contactId}
          onClose={() => setDealVorbereitung(null)}
          onSaved={() => {
            setDealVorbereitung(null);
          }}
        />
      )}
    </div>
  );
}
